"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useCallback, useMemo, useRef } from "react";
import useCapacitor from "@/hooks/useCapacitor";
import { useDebouncedCallback } from "use-debounce";
export enum CreateDropWaveWrapperContext {
  WAVE_CHAT = "WAVE_CHAT",
  SINGLE_DROP = "SINGLE_DROP",
  MY_STREAM = "MY_STREAM",
}
interface CreateDropWaveWrapperProps {
  readonly children: React.ReactNode;
  readonly context?: CreateDropWaveWrapperContext;
}
function useResizeObserver(containerRef, fixedBottomRef) {
  const $ = _c(8);
  let t0;
  if ($[0] !== containerRef || $[1] !== fixedBottomRef) {
    t0 = () => {
      const container = containerRef.current;
      const fixedBottom = fixedBottomRef.current;
      if (!container || !fixedBottom) {
        return;
      }
      const containerRect = container.getBoundingClientRect();
      const fixedBottomRect = fixedBottom.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      if (fixedBottomRect.bottom > viewportHeight) {
        window.scrollTo({
          top: window.scrollY + (fixedBottomRect.bottom - viewportHeight) + 20,
          behavior: "smooth"
        });
      } else {
        if (fixedBottomRect.bottom < viewportHeight) {
          const newScrollTop = Math.max(0, window.scrollY - (viewportHeight - fixedBottomRect.bottom) + 20);
          window.scrollTo({
            top: newScrollTop,
            behavior: "smooth"
          });
        }
      }
      if (containerRect.top < 0) {
        window.scrollTo({
          top: window.scrollY + containerRect.top,
          behavior: "smooth"
        });
      }
    };
    $[0] = containerRef;
    $[1] = fixedBottomRef;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const handleResize = t0;
  const debouncedHandleResize = useDebouncedCallback(handleResize, 100);
  let t1;
  let t2;
  if ($[3] !== containerRef || $[4] !== debouncedHandleResize || $[5] !== fixedBottomRef) {
    t1 = () => {
      if (!containerRef.current || !fixedBottomRef.current) {
        return;
      }
      const observer = new ResizeObserver(debouncedHandleResize);
      observer.observe(containerRef.current);
      observer.observe(fixedBottomRef.current);
      return () => observer.disconnect();
    };
    t2 = [debouncedHandleResize, containerRef, fixedBottomRef];
    $[3] = containerRef;
    $[4] = debouncedHandleResize;
    $[5] = fixedBottomRef;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
}
export function CreateDropWaveWrapper(t0) {
  const $ = _c(4);
  const {
    children,
    context: t1
  } = t0;
  const context = t1 === undefined ? CreateDropWaveWrapperContext.WAVE_CHAT : t1;
  const capacitor = useCapacitor();
  const containerRef = useRef(null);
  const fixedBottomRef = useRef(null);
  useResizeObserver(containerRef, fixedBottomRef);
  let t2;
  bb0: {
    const isMyStreamOrWaveChat = context === CreateDropWaveWrapperContext.MY_STREAM || context === CreateDropWaveWrapperContext.WAVE_CHAT;
    if (capacitor.isIos) {
      const marginClass = isMyStreamOrWaveChat && !capacitor.keyboardVisible ? "tw-mb-[3.75rem]" : "";
      t2 = `tw-max-h-[calc(100vh-14.7rem)] ${marginClass} tw-z-[998]`;
      break bb0;
    }
    t2 = "tw-max-h-[calc(100vh-8.5rem)] lg:tw-max-h-[calc(100vh-7.5rem)] tw-z-30";
  }
  const containerClassName = t2;
  const t3 = `${containerClassName} tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 hover:tw-scrollbar-thumb-iron-300 tw-py-2 tw-px-4 tw-top-0 tw-sticky tw-w-full tw-rounded-b-xl tw-flex-none tw-transition-colors tw-duration-500 tw-border-t tw-border-solid tw-border-x-0 tw-border-b-0 tw-border-iron-800 tw-bg-iron-950`;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div ref={fixedBottomRef} />;
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  let t5;
  if ($[1] !== children || $[2] !== t3) {
    t5 = <div ref={containerRef} className={t3}>{children}{t4}</div>;
    $[1] = children;
    $[2] = t3;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  return t5;
}